// frontend/src/pages/NotificationPreferences.js
/**
 * Notification Preferences Page
 * 
 * Matrix-style interface for managing notification delivery preferences.
 * Features:
 * - Grouped by category (Leave, Attendance, Documents, etc.)
 * - Toggle switches for each notification type and delivery method
 * - In-app notifications cannot be disabled
 * - System defaults for new notification types
 * - Optimistic updates with React Query
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Settings,
  Save,
  X,
  Calendar,
  Clock,
  FileText,
  Wallet,
  AlertTriangle,
  UserCheck,
  Megaphone,
  Mail,
  MessageSquare,
  CheckCircle2,
  MessageCircle,
  ShieldOff,
  Package,
  Lock,
  Cake,
  Moon,
  CalendarClock,
} from 'lucide-react';
import { useNotificationPreferences } from '../hooks/useNotificationPreferences';
import toast from '../utils/toast';

// Notification categories with icons
const CATEGORIES = [
  { value: 'leave', label: 'Leave & Time-Off', icon: Calendar, color: 'blue' },
  { value: 'attendance', label: 'Attendance', icon: Clock, color: 'orange' },
  { value: 'documents', label: 'Documents & Certificates', icon: FileText, color: 'purple' },
  { value: 'payroll', label: 'Payroll', icon: Wallet, color: 'green' },
  { value: 'compliance', label: 'Compliance Alerts', icon: AlertTriangle, color: 'red' },
  { value: 'employment', label: 'Employment Status', icon: UserCheck, color: 'indigo' },
  { value: 'nightwork', label: 'Nightwork', icon: Moon, color: 'violet' },
  { value: 'restday_work', label: 'Rest Day Work', icon: CalendarClock, color: 'amber' },
  { value: 'assets', label: 'Assets', icon: Package, color: 'teal' },
  { value: 'celebrations', label: 'Celebrations', icon: Cake, color: 'pink' },
  { value: 'system', label: 'System Announcements', icon: Megaphone, color: 'gray' },
];

// Notification types grouped by category
const NOTIFICATION_TYPES = {
  leave: [
    { value: 'leave_approved', label: 'Leave Approved' },
    { value: 'leave_rejected', label: 'Leave Rejected' },
    { value: 'leave_pending_approval', label: 'Leave Pending Approval' },
    { value: 'leave_escalated', label: 'Leave Approval Escalated' },
    { value: 'leave_cancelled_notice', label: 'Leave Cancelled Notice' },
    { value: 'oil_credited', label: 'Off in Lieu Credited' },
    { value: 'oil_expiring', label: 'OIL Expiring Soon' },
    { value: 'oil_forfeited', label: 'OIL Forfeited' },
    { value: 'al_planning_reminder', label: 'Annual Leave Planning Reminder' },
    { value: 'al_forfeiture_warning', label: 'Annual Leave Forfeiture Warning' },
  ],
  attendance: [
    { value: 'missing_clockin', label: 'Missing Clock-In Reminder' },
    { value: 'missing_clockin_pm', label: 'Missing Clock-In Reminder (PM Session)' },
    { value: 'clockout_soft_reminder', label: 'Clock-Out Reminder' },
    { value: 'clockout_urgent_reminder', label: 'Urgent Clock-Out Reminder' },
    { value: 'clockout_final_warning', label: 'Final Clock-Out Warning' },
    { value: 'exception_flagged', label: 'Attendance Exception Flagged' },
    { value: 'exception_resolved', label: 'Attendance Exception Resolved' },
  ],
  documents: [
    { value: 'document_request_approved', label: 'Document Request Approved' },
    { value: 'document_request_rejected', label: 'Document Request Rejected' },
    { value: 'document_request_cancelled', label: 'Document Request Cancelled' },
    { value: 'hr_letter_ready', label: 'HR Letter Ready' },
    { value: 'certificate_expiring', label: 'Certificate Expiring' },
    { value: 'document_approval_update', label: 'Document Approval Update' },
  ],
  payroll: [
    { value: 'payslip_available', label: 'Payslip Available' },
  ],
  compliance: [
    { value: 'overtime_at_risk', label: 'Overtime At Risk (48hr)' },
    { value: 'overtime_critical', label: 'Overtime Critical (60hr)' },
    { value: 'overtime_exceeded', label: 'Overtime Limit Exceeded (72hr)' },
    { value: 'rest_day_watch', label: 'Rest Day Reminder (7d)' },
    { value: 'rest_day_warning', label: 'Rest Day Warning (10d)' },
    { value: 'rest_day_critical', label: 'Rest Day Critical (12d)' },
    { value: 'rest_day_violated', label: 'Rest Day Limit Exceeded (12d+)' },
  ],
  employment: [
    { value: 'probation_ending', label: 'Probation Ending' },
    { value: 'contract_ending', label: 'Contract Ending' },
    { value: 'onboarding_reminder', label: 'Onboarding Task Reminder' },
    { value: 'offboarding_reminder', label: 'Offboarding Task Reminder' },
  ],
  assets: [
    { value: 'asset_return_pending', label: 'Asset Return Pending' },
    { value: 'asset_return_confirmed', label: 'Asset Return Confirmed' },
    { value: 'asset_return_rejected', label: 'Asset Return Rejected' },
    { value: 'asset_return_cancelled', label: 'Asset Return Cancelled' },
    { value: 'asset_return_expired', label: 'Asset Return Expired' },
    { value: 'asset_transfer_incoming', label: 'Incoming Asset Transfer' },
    { value: 'asset_transfer_accepted', label: 'Asset Transfer Accepted' },
    { value: 'asset_transfer_rejected', label: 'Asset Transfer Rejected' },
    { value: 'asset_transfer_cancelled', label: 'Asset Transfer Cancelled' },
    { value: 'asset_transfer_expired', label: 'Asset Transfer Expired' },
    { value: 'asset_receipt_pending', label: 'Asset Receipt Pending' },
    { value: 'asset_receipt_acknowledged', label: 'Asset Receipt Acknowledged' },
  ],
  nightwork: [
    { value: 'nightwork_submitted', label: 'Nightwork Request Submitted' },
    { value: 'nightwork_approved', label: 'Nightwork Request Approved' },
    { value: 'nightwork_fully_approved', label: 'Nightwork Fully Approved' },
    { value: 'nightwork_rejected', label: 'Nightwork Request Rejected' },
    { value: 'nightwork_cancelled', label: 'Nightwork Request Cancelled' },
    { value: 'nightwork_assigned', label: 'Nightwork Assignment' },
    { value: 'nightwork_amended', label: 'Nightwork Request Amended' },
  ],
  restday_work: [
    { value: 'restday_work_submitted', label: 'Rest Day Work Request Submitted' },
    { value: 'restday_work_approved', label: 'Rest Day Work Request Approved' },
    { value: 'restday_work_fully_approved', label: 'Rest Day Work Fully Approved' },
    { value: 'restday_work_rejected', label: 'Rest Day Work Request Rejected' },
    { value: 'restday_work_cancelled', label: 'Rest Day Work Request Cancelled' },
    { value: 'restday_work_assigned', label: 'Rest Day Work Assignment' },
    { value: 'restday_work_amended', label: 'Rest Day Work Request Amended' },
  ],
  celebrations: [
    { value: 'birthday_wishes', label: 'Birthday Wishes' },
  ],
  system: [
    { value: 'system_maintenance', label: 'System Maintenance' },
    { value: 'policy_update', label: 'Policy Update' },
    { value: 'general_announcement', label: 'General Announcement' },
  ],
};

// Delivery methods
const DELIVERY_METHODS = [
  { value: 'in_app', label: 'In-App', icon: Bell, disabled: true, description: 'Always enabled' },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, disabled: false, description: 'Requires phone number' },
  { value: 'email', label: 'Email', icon: Mail, disabled: false, description: 'Requires email address' },
  { value: 'google_chat', label: 'Google Chat', icon: MessageCircle, disabled: false, description: 'Requires Google account' },
];

const CATEGORY_STYLES = {
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-800' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800' },
  green:  { bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-800' },
  red:    { bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-800' },
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-800' },
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-800' },
  amber:  { bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-800' },
  teal:   { bg: 'bg-teal-50',   border: 'border-teal-200',   text: 'text-teal-800' },
  pink:   { bg: 'bg-pink-50',   border: 'border-pink-200',   text: 'text-pink-800' },
  gray:   { bg: 'bg-gray-50',   border: 'border-gray-200',   text: 'text-gray-800' },
};

const NotificationPreferences = () => {
  const navigate = useNavigate();
  const { preferences, disabledByPolicy, lockedByAdmin, birthdayWishOptOut, loading, error, updatePreferences, toggleBirthdayOptOut, isUpdating, isBirthdayUpdating } = useNotificationPreferences();
  const [localPreferences, setLocalPreferences] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local preferences from API data
  React.useEffect(() => {
    if (preferences && Object.keys(preferences).length > 0) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  // Check if there are unsaved changes
  React.useEffect(() => {
    const changed = JSON.stringify(localPreferences) !== JSON.stringify(preferences);
    setHasChanges(changed);
  }, [localPreferences, preferences]);

  // Get preference value for a notification type and method
  const getPreference = (notifType, method) => {
    // In-app is always enabled
    if (method === 'in_app') return true;
    
    const typePrefs = localPreferences[notifType] || preferences[notifType] || {};
    return typePrefs[method] || false;
  };

  const isDisabledByPolicy = (notifType) => disabledByPolicy.includes(notifType);

  const isChannelLocked = (notifType, method) => {
    const typeLocks = lockedByAdmin[notifType];
    if (!typeLocks) return false;
    return !!typeLocks[method];
  };

  // Toggle preference
  const handleToggle = (notifType, method) => {
    if (method === 'in_app') return;
    if (isDisabledByPolicy(notifType)) return;
    if (isChannelLocked(notifType, method)) return;
    
    setLocalPreferences((prev) => {
      const newPrefs = { ...prev };
      if (!newPrefs[notifType]) {
        newPrefs[notifType] = {};
      }
      
      const newValue = !getPreference(notifType, method);
      newPrefs[notifType] = {
        ...newPrefs[notifType],
        [method]: newValue,
      };
      
      return newPrefs;
    });
  };

  // Handle save
  const handleSave = () => {
    updatePreferences(localPreferences);
  };

  // Handle cancel
  const handleCancel = () => {
    setLocalPreferences(preferences || {});
    setHasChanges(false);
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find((c) => c.value === category);
    return cat ? cat.icon : Bell;
  };

  if (loading) {
    return (
      <div className="space-y-6 bg-gray-50 min-h-screen">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
            <div className="space-y-2">
              <div className="h-7 bg-gray-200 rounded w-64 animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-96 animate-pulse"></div>
            </div>
          </div>
          <div className="h-10 bg-gray-200 rounded-full w-24 animate-pulse"></div>
        </div>

        {/* Action Bar Skeleton (conditional - matches hasChanges state) */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 animate-pulse">
          <div className="flex items-center justify-between">
            <div className="h-5 bg-gray-200 rounded w-48"></div>
            <div className="flex gap-2">
              <div className="h-10 bg-gray-200 rounded-full w-24"></div>
              <div className="h-10 bg-gray-200 rounded-full w-32"></div>
            </div>
          </div>
        </div>

        {/* Preferences Matrix Skeleton */}
        <div className="space-y-6">
          {[1, 2, 3, 4].map(categoryIndex => (
            <div key={categoryIndex} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Category Header Skeleton */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                <div className="flex items-center gap-3 animate-pulse">
                  <div className="w-5 h-5 bg-gray-200 rounded"></div>
                  <div className="h-6 bg-gray-200 rounded w-48"></div>
                </div>
              </div>

              {/* Table Skeleton */}
              <div className="overflow-x-auto">
                <div className="min-w-full">
                  {/* Table Header Skeleton */}
                  <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
                    <div className="grid grid-cols-5 gap-4 animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="flex flex-col items-center gap-1">
                          <div className="w-4 h-4 bg-gray-200 rounded"></div>
                          <div className="h-3 bg-gray-200 rounded w-16"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Table Rows Skeleton */}
                  <div className="divide-y divide-gray-100">
                    {[1, 2, 3, 4].map(rowIndex => (
                      <div key={rowIndex} className="px-6 py-4 animate-pulse">
                        <div className="grid grid-cols-5 gap-4 items-center">
                          <div className="h-4 bg-gray-200 rounded w-40"></div>
                          {[1, 2, 3, 4].map(i => (
                            <div key={i} className="flex justify-center">
                              <div className="h-6 w-11 bg-gray-200 rounded-full"></div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Info Footer Skeleton */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 animate-pulse">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-gray-200 rounded mt-0.5"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-48"></div>
              <div className="space-y-1">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-3 bg-gray-200 rounded w-full"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading preferences: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center">
            <Settings className="w-5 h-5 text-blue-800" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Notification Preferences</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Choose how you want to receive notifications for each type
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/notifications')}
          className="btn btn-secondary self-end sm:self-auto"
        >
          <X className="w-4 h-4 mr-2" />
          Close
        </button>
      </div>

      {/* Action Bar */}
      {hasChanges && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-blue-800 flex-shrink-0" />
            <p className="text-sm text-blue-800 font-medium">You have unsaved changes</p>
          </div>
          <div className="flex gap-2 self-end sm:self-auto">
            <button
              onClick={handleCancel}
              className="btn btn-secondary self-center sm:self-auto"
              disabled={isUpdating}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="btn btn-primary self-center sm:self-auto"
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Preferences Matrix */}
      <div className="space-y-6">
        {CATEGORIES.map((category) => {
          const types = NOTIFICATION_TYPES[category.value] || [];
          if (types.length === 0) return null;

          const CategoryIcon = category.icon;
          const styles = CATEGORY_STYLES[category.color];

          return (
            <div key={category.value} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Category Header */}
              <div className={`${styles.bg} border-b ${styles.border} px-4 md:px-6 py-3 md:py-4`}>
                <div className="flex items-center gap-3">
                  <CategoryIcon className={`w-5 h-5 ${styles.text}`} />
                  <h2 className="text-base md:text-lg font-semibold text-gray-900">{category.label}</h2>
                </div>
              </div>

              {/* Mobile Card Layout */}
              <div className="block md:hidden divide-y divide-gray-100">
                {types.map((type) => {
                  const policyBlocked = isDisabledByPolicy(type.value);
                  return (
                    <div key={type.value} className={`px-4 py-3 ${policyBlocked ? 'bg-gray-50' : ''}`}>
                      <div className="flex items-center gap-2 mb-2.5">
                        <span className={`text-sm font-medium ${policyBlocked ? 'text-gray-400' : 'text-gray-900'}`}>
                          {type.label}
                        </span>
                        {policyBlocked && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                            <ShieldOff className="w-3.5 h-3.5" />
                            Disabled
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {DELIVERY_METHODS.map((method) => {
                          const locked = isChannelLocked(type.value, method.value);
                          const isEnabled = policyBlocked ? false : getPreference(type.value, method.value);
                          const isDisabled = method.disabled || policyBlocked || locked;
                          const MethodIcon = method.icon;
                          return (
                            <div
                              key={method.value}
                              className={`flex items-center justify-between px-3 py-2 rounded-lg ${isEnabled && !policyBlocked ? 'bg-blue-50' : 'bg-gray-50'}`}
                            >
                              <div className="flex items-center gap-1.5 min-w-0">
                                <MethodIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                <span className="text-xs text-gray-600 truncate">{method.label}</span>
                                {locked && !policyBlocked && (
                                  <span className="p-1 flex-shrink-0">
                                    <Lock className="w-3 h-3 text-amber-500" />
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => handleToggle(type.value, method.value)}
                                disabled={isDisabled || isUpdating}
                                role="switch"
                                aria-checked={isEnabled}
                                aria-label={`${method.label} notifications for ${type.label}`}
                                className={`
                                  relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors
                                  before:absolute before:-inset-3 before:content-['']
                                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
                                  ${isEnabled ? 'bg-blue-800' : 'bg-gray-300'}
                                  ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                `}
                              >
                                <span
                                  className={`
                                    inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm
                                    ${isEnabled ? 'translate-x-[19px]' : 'translate-x-[3px]'}
                                  `}
                                />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Matrix Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full min-w-[500px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 lg:px-6 py-3 text-sm font-semibold text-gray-700 w-[40%] align-middle">
                        Notification Type
                      </th>
                      {DELIVERY_METHODS.map((method) => (
                        <th
                          key={method.value}
                          className="text-center px-2 py-3 text-sm font-semibold text-gray-700 align-middle"
                        >
                          <div className="flex flex-col items-center gap-1">
                            <method.icon className="w-4 h-4 text-gray-600" />
                            <span>{method.label}</span>
                            {method.description && (
                              <span className="text-xs text-gray-400 font-normal hidden lg:block">
                                {method.description}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {types.map((type) => {
                      const policyBlocked = isDisabledByPolicy(type.value);
                      return (
                        <tr
                          key={type.value}
                          className={`${policyBlocked ? 'bg-gray-50' : 'table-row'}`}
                        >
                          <td className={`px-4 lg:px-6 py-4 text-sm align-middle ${policyBlocked ? 'text-gray-400' : 'text-gray-900'}`}>
                            <div className="flex items-center gap-2">
                              {type.label}
                              {policyBlocked && (
                                <span
                                  className="inline-flex items-center gap-1 text-xs text-gray-400 font-medium"
                                  title="This notification type has been disabled by your organization's admin policy"
                                >
                                  <ShieldOff className="w-3.5 h-3.5" />
                                  Disabled by policy
                                </span>
                              )}
                            </div>
                          </td>
                          {DELIVERY_METHODS.map((method) => {
                            const locked = isChannelLocked(type.value, method.value);
                            const isEnabled = policyBlocked ? false : getPreference(type.value, method.value);
                            const isDisabled = method.disabled || policyBlocked || locked;
                            
                            return (
                              <td key={method.value} className="px-2 py-4 align-middle">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => handleToggle(type.value, method.value)}
                                    disabled={isDisabled || isUpdating}
                                    role="switch"
                                    aria-checked={isEnabled}
                                    aria-label={`${method.label} notifications for ${type.label}`}
                                    className={`
                                      relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                                      before:absolute before:-inset-[10px] before:content-['']
                                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
                                      ${isEnabled ? 'bg-blue-800' : 'bg-gray-200'}
                                      ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                    `}
                                  >
                                    <span
                                      className={`
                                        inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm
                                        ${isEnabled ? 'translate-x-6' : 'translate-x-1'}
                                      `}
                                    />
                                  </button>
                                  {locked && !policyBlocked && (
                                    <span className="p-1 flex-shrink-0" title="Locked by admin">
                                      <Lock className="w-3 h-3 text-amber-500" />
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {/* Birthday Wishes Opt-Out */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-amber-50 border-b border-amber-200 px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center gap-3">
            <Cake className="w-5 h-5 text-amber-700" />
            <h2 className="text-base md:text-lg font-semibold text-gray-900">Birthday Wishes</h2>
          </div>
        </div>
        <div className="px-4 md:px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex-1">
              <p className="text-sm text-gray-900 font-medium">Include me in company birthday wishes</p>
              <p className="text-xs text-gray-500 mt-1">
                A company-wide email is sent on your birthday to celebrate with the team. You may opt out if you prefer not to be featured.
              </p>
            </div>
            <button
              onClick={() => toggleBirthdayOptOut(!birthdayWishOptOut)}
              disabled={isBirthdayUpdating}
              role="switch"
              aria-checked={!birthdayWishOptOut}
              aria-label="Include me in birthday wishes"
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors
                before:absolute before:-inset-[10px] before:content-['']
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
                ${!birthdayWishOptOut ? 'bg-blue-800' : 'bg-gray-200'}
                ${isBirthdayUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm
                  ${!birthdayWishOptOut ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Info Footer */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Bell className="w-5 h-5 text-blue-800 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-blue-800 font-medium mb-1">About Notification Preferences</p>
            <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
              <li>In-app notifications are always enabled and cannot be disabled</li>
              <li>WhatsApp notifications require a valid phone number in your profile</li>
              <li>Email notifications require a valid email address in your profile</li>
              <li>Channels showing a lock icon have been enforced by your admin and cannot be changed here</li>
              <li>Types marked "Disabled by policy" have been turned off by your organization's admin and cannot be changed here</li>
              <li>Changes are saved automatically when you click "Save Changes"</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;

